import CommonTitleBar from "components/common/CommonTitleBar";
import DeleteConfirmationModal from "components/common/ConfirmationDialog";
import CustomField from "components/common/CustomField";
import CustomLabel from "components/common/CustomLabel";
import Filters from "components/common/Filters";
import IsLoading from "components/common/IsLoading";
import NoDataFound from "components/common/NoDataFound";
import Paginations from "components/common/Paginations";
import Spinners from "components/common/Spinners";
import {
  getKeywordValue,
  getMinMaxDate,
  handleStopPropagation,
} from "components/common/Utils";
import { addCitySchema } from "components/common/Validation";
import { orderByOptions, statusOptions } from "components/constants/StatusKeys";
import TableShimmer from "components/ShimmerEffects/CommonShimmer/TableShimmer";
import { Form, Formik } from "formik";
import { Ellipsis, X } from "lucide-react";
import { InputSwitch } from "primereact/inputswitch";
import React, { useEffect, useState } from "react";
import {
  Badge,
  Col,
  Container,
  Dropdown,
  Modal,
  Row,
  Table,
} from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  addCity,
  deleteCity,
  getAllCities,
  getCityDetails,
  updateCity,
  updateCityStatus,
} from "store/actions/citySlice";
import { showAlert } from "store/reducers/alertReducer";

const Cities = () => {
  const { t } = useTranslation();
  document.title = t("Common | Work Space");

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { search } = useLocation();
  const pageNumber = Number(new URLSearchParams(search).get("page")) || 1;

  const {
    isLoading,
    loading,
    perPage,
    totalCount,
    totalPages,
    currentPage,
    cities,
    cityDetails,
    gettingDetails,
  } = useSelector((state) => state.Cities);

  const [selectedId, setSelectedId] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [modalType, setModalType] = useState(null);
  const [show, setShow] = useState(false); // ADD/EDIT MODAL
  const [showModal, setShowModal] = useState(false); // DELETE MODAL

  const [keyword, setKeyword] = useState("");

  const [formValues, setFormValues] = useState({
    name: null,
    nameAr: null,
  });

  const [dates, setDates] = useState([]);

  const [selectedFilters, setSelectedFilters] = useState({
    status: -1,
    orderBy: -1,
  });

  const handleClose = () => {
    setShow(false);
    setModalType(null);
    setFormValues({
      name: null,
      nameAr: null,
    });
  };
  const handleShow = () => setShow(true);

  useEffect(() => {
    getAllData();
  }, []);

  const getAllData = (page, action) => {
    const { actionType } = action || {}; // SEARCH, RESET, RESET_KEYWORD
    const { status, orderBy } = selectedFilters;
    const isReset = actionType === "RESET";

    const payload = {
      perPage,
      pageNo: page || pageNumber,
      status: isReset ? -1 : status,
      orderBy: isReset ? -1 : orderBy,
      dateRange: isReset ? {} : getMinMaxDate(dates),
      keyword: getKeywordValue(actionType, keyword),
    };
    dispatch(getAllCities(payload));
    navigate(`/management/cities?page=${page || pageNumber}`);
  };

  const handleStatusChange = (id, status) => {
    setSelectedId(id);
    const payload = {
      id,
      status: status === 1 ? 0 : 1,
    };

    dispatch(updateCityStatus(payload))
      .then((action) => {
        const { statusCode, success, message } = action?.payload;
        if (statusCode === 200 && success) {
          dispatch(showAlert({ text: message, type: "success" }));
          setSelectedId(null);
        } else {
          dispatch(showAlert({ text: message, type: "error" }));
          setSelectedId(null);
        }
      })
      .catch((err) => {});
  };

  useEffect(() => {
    const getDetails = () => {
      const payload = {
        id: selectedId,
      };
      dispatch(getCityDetails(payload));
    };
    if (modalType === "EDIT") {
      getDetails();
    }
  }, [modalType]);

  // SET FORM FIELD
  useEffect(() => {
    if (cityDetails && !gettingDetails) {
      const { name, nameAr } = cityDetails || {};
      setFormValues({
        name,
        nameAr,
      });
    }
  }, [cityDetails, gettingDetails]);

  const handleSubmit = (values, { resetForm }) => {
    const { name, nameAr } = values;

    const payload = {
      name,
      nameAr,
    };

    if (modalType === "ADD") {
      dispatch(addCity(payload))
        .then((action) => {
          const { statusCode, success, message } = action?.payload;
          if (statusCode === 200 && success) {
            dispatch(showAlert({ text: message, type: "success" }));
            handleClose();
            resetForm();
          } else {
            dispatch(showAlert({ text: message, type: "error" }));
            handleClose();
          }
        })
        .catch((err) => {});
    } else if (modalType === "EDIT") {
      const updatedPayload = {
        ...payload,
        id: selectedId,
      };
      dispatch(updateCity(updatedPayload))
        .then((action) => {
          const { statusCode, success, message } = action?.payload || {};
          if (statusCode === 200 && success) {
            dispatch(showAlert({ text: message, type: "success" }));
            handleClose();
            resetForm();
          } else {
            dispatch(showAlert({ text: message, type: "error" }));
            handleClose();
          }
        })
        .catch((err) => {});
    }
  };

  const handleDelete = () => {
    const payload = {
      id: selectedId,
      status: "2", // 2 FOR DELETE
    };
    dispatch(deleteCity(payload))
      .then((action) => {
        const { statusCode, success, message } = action?.payload;
        if (statusCode === 200 && success) {
          dispatch(showAlert({ text: message, type: "success" }));
          setShowModal(false);
          setSelectedId(null);
        } else {
          dispatch(showAlert({ text: message, type: "error" }));
        }
      })
      .catch((err) => {});
  };

  const handleReset = () => {
    setKeyword("");
    setDates([]);
    setSelectedFilters({ orderBy: -1, status: -1 });
    getAllData(pageNumber, { actionType: "RESET" });
  };

  const filters = [
    {
      key: "status",
      placeholder: "Status",
      options: statusOptions,
      type: "singleSelect",
    },
    {
      key: "orderBy",
      placeholder: "Order By",
      options: orderByOptions,
      type: "singleSelect",
    },
  ];

  return (
    <React.Fragment>
      <div className="page-content city_page common_page">
        <Container fluid>
          <CommonTitleBar
            title={t("Cities")}
            isCreateBtn={true}
            createBtnText={t("Add New")}
            onCreateBtnClick={() => {
              setModalType("ADD");
              handleShow();
            }}
          />
          <div className="common_wrapper">
            <Filters
              keyword={keyword}
              setKeyword={setKeyword}
              selectedFilters={selectedFilters}
              setSelectedFilters={setSelectedFilters}
              dates={dates}
              setDates={setDates}
              filters={filters}
              onSearch={getAllData}
              onReset={handleReset}
            />
            <Row>
              <Col lg={12}>
                {loading ? (
                  <TableShimmer col={5} />
                ) : cities.length === 0 ? (
                  <NoDataFound />
                ) : (
                  <div
                    className="common_table_style"
                    style={{ minHeight: showDropdown ? 210 : "unset" }}
                  >
                    <Table>
                      <thead className="t_head">
                        <tr>
                          <th>#</th>
                          <th>{t("Name(English)")}</th>
                          <th>{t("Name(Arabic)")}</th>
                          <th>{t("Status")}</th>
                          <th>{t("Actions")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cities?.map((item, index) => (
                          <tr key={item?._id}>
                            <td>{(currentPage - 1) * perPage + index + 1}</td>
                            <td>{item.name}</td>
                            <td>{item.nameAr}</td>
                            <td>
                              <Badge
                                bg={
                                  item.status === 1
                                    ? " bg_primary"
                                    : " bg-secondary"
                                }
                              >
                                {item.status === 1
                                  ? t("Active")
                                  : t("Deactive")}
                              </Badge>
                            </td>
                            <td>
                              <div className="d-flex align-items-center gap-3">
                                <InputSwitch
                                  checked={item.status === 1}
                                  disabled={
                                    isLoading && selectedId === item._id
                                  }
                                  onChange={(e) =>
                                    handleStatusChange(item._id, item.status)
                                  }
                                />
                                <Dropdown
                                  className="custom_dropdown list_action_dropdown"
                                  drop={"down"}
                                  onClick={handleStopPropagation}
                                  autoClose="outside"
                                  onToggle={(isOpen) => setShowDropdown(isOpen)}
                                >
                                  <Dropdown.Toggle>
                                    <Ellipsis className="text_black" />
                                  </Dropdown.Toggle>

                                  <Dropdown.Menu align="end">
                                    <Dropdown.Item
                                      href="#"
                                      onClick={() => {
                                        setSelectedId(item._id);
                                        setModalType("EDIT");
                                        handleShow();
                                      }}
                                    >
                                      {t("Edit")}
                                    </Dropdown.Item>
                                    <Dropdown.Item
                                      href="#"
                                      className="text_danger"
                                      onClick={() => {
                                        setSelectedId(item._id);
                                        setShowModal(true);
                                      }}
                                    >
                                      {t("Delete")}
                                    </Dropdown.Item>
                                  </Dropdown.Menu>
                                </Dropdown>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Col>
            </Row>
          </div>
          {!loading && (
            <Paginations
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={(page) => {
                getAllData(page);
              }}
              totalCount={totalCount}
            />
          )}
        </Container>
      </div>
      <Modal centered show={show} onHide={handleClose} className="common_modal">
        <div className="modal_header g_between">
          <h2 className="fs_17">
            {modalType === "ADD" ? t("Add City") : t("Edit City")}
          </h2>
          <X
            onClick={handleClose}
            className="text_black c_p"
            width={20}
            height={20}
          />
        </div>
        <div className="modal_body mt-0">
          {modalType === "EDIT" && gettingDetails ? (
            <IsLoading />
          ) : (
            <div>
              <Formik
                initialValues={formValues}
                validationSchema={addCitySchema}
                enableReinitialize={true}
                onSubmit={handleSubmit}
              >
                {({ errors, touched }) => (
                  <Form>
                    <Row>
                      <Col lg={12}>
                        <div className="form_field mt_13 mb-0">
                          <CustomLabel
                            htmlFor="name"
                            label={t("English Name")}
                          />
                          <CustomField
                            name="name"
                            type="text"
                            placeholder={t("Enter English Name")}
                            touched={touched}
                            errors={errors}
                          />
                        </div>
                      </Col>
                      <Col lg={12}>
                        <div className="form_field mt_13 mb-0">
                          <CustomLabel
                            htmlFor="name"
                            label={t("Arabic Name")}
                          />
                          <CustomField
                            name="nameAr"
                            type="text"
                            placeholder={t("Enter Arabic Name")}
                            touched={touched}
                            errors={errors}
                            isArabic={true}
                          />
                        </div>
                      </Col>
                      <Col lg={12}>
                        <div className="g_between mt-4 gap-3">
                          <button
                            type="button"
                            className="cancel_btn w-50"
                            onClick={handleClose}
                          >
                            {t("Cancel")}
                          </button>
                          <button
                            type="submit"
                            className="main_btn w-50"
                            disabled={isLoading}
                          >
                            {modalType === "ADD" ? t("Add") : t("Save")}
                          </button>
                        </div>
                      </Col>
                    </Row>
                  </Form>
                )}
              </Formik>
            </div>
          )}
        </div>
      </Modal>
      <DeleteConfirmationModal
        show={showModal}
        onClose={() => {
          setShowModal(false);
          setSelectedId(null);
        }}
        onConfirm={handleDelete}
        message={t("Are you sure you want to delete this {{name}}?", {
          name: "City",
        })}
        isLoading={isLoading}
      />
    </React.Fragment>
  );
};

export default Cities;
